import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Loader2, FileText, Lock } from 'lucide-react';
import { InquiryPublicDto } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useInquiries } from '@/hooks/useInquiry';
import {
  filterInquiriesForNewSessionPicker,
  isNdaPending,
} from '@/lib/llm-chat-selectable-inquiries';

interface NewLlmChatSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateSession: (inquiryId: string) => Promise<void>;
}

export function NewLlmChatSessionDialog({
  open,
  onOpenChange,
  onCreateSession,
}: NewLlmChatSessionDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { data: allInquiries = [], isLoading } = useInquiries();
  const inquiries = filterInquiriesForNewSessionPicker(allInquiries as InquiryPublicDto[]);

  const handleCreate = async () => {
    if (!selected) return;
    setIsCreating(true);
    try {
      await onCreateSession(selected);
      onOpenChange(false);
      setSelected(null);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('llmChat.selectInquiry')}</DialogTitle>
          <DialogDescription>{t('llmChat.selectInquiryDescription')}</DialogDescription>
        </DialogHeader>

        <div className="max-h-80 space-y-2 overflow-y-auto py-4">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isLoading && inquiries.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('llmChat.noInquiries')}
            </p>
          )}
          {inquiries.map((inquiry) => {
            const locked = isNdaPending(inquiry as InquiryPublicDto);
            return (
              <button
                key={inquiry.id}
                onClick={() => !locked && setSelected(inquiry.id)}
                disabled={locked}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors',
                  locked
                    ? 'cursor-not-allowed opacity-60'
                    : 'hover:bg-accent',
                  !locked && selected === inquiry.id && 'border-primary bg-primary/5',
                )}
              >
                {locked ? (
                  <Lock className="h-5 w-5 shrink-0 text-brand-primary" />
                ) : (
                  <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{inquiry.title}</div>
                  <div className="mt-0.5 text-sm text-muted-foreground">
                    {inquiry.category?.name}
                  </div>
                </div>
                {locked && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenChange(false);
                      navigate(`/inquiries/${inquiry.id}`);
                    }}
                  >
                    {t('llmChat.acceptNda')}
                  </Button>
                )}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            {t('llmChat.cancel')}
          </Button>
          <Button onClick={handleCreate} disabled={!selected || isCreating}>
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('llmChat.startSession')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
